import { useState } from "react";
import { useRouteParam } from "@/shared/routing/useRouteParam.ts";
import { parseIsoDate } from "@/shared/utils/Dates.ts";
import type { LaborCostChartMode } from "@/pages/human-resources/time-management/business/analysis/LaborCostChartMode.ts";
import { useReadLaborCostChartData } from "@/pages/human-resources/time-management/services/hooks/useReadLaborCostChartData.ts";
import type { LaborCostChartData } from "@/pages/human-resources/time-management/business/analysis/LaborCostChartData.ts";
import useScheduleReferenceData from "@/pages/human-resources/time-management/schedules/reference-provider/useScheduleReferenceData.ts";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates.ts";
import useScheduleRangeOnRoute from "@/pages/human-resources/time-management/hooks/useScheduleRangeOnRoute.ts";
import useScheduleUserPermissions from "@/pages/human-resources/time-management/hooks/useScheduleUserPermissions.ts";

export interface UseLaborCostCardController {
  mode: LaborCostChartMode;
  setMode: (m: LaborCostChartMode) => void;
  selectedServiceIndex: number | undefined;
  setSelectedServiceIndex: (v: number | undefined) => void;
  isFetching: boolean;
  chartData: LaborCostChartData | undefined;
  isEnabled: boolean;
}

export function useLaborCostCardController(): UseLaborCostCardController {
  const [mode, setMode] = useState<LaborCostChartMode>("SECTION");
  const [selectedServiceIndex, setSelectedServiceIndex] = useState<
    number | undefined
  >(undefined);

  const {
    domainData: { domainId },
    siteData: { siteId },
  } = useScheduleReferenceData();
  const { canSeeSalaries } = useScheduleUserPermissions();
  const dateStr = useRouteParam("date");
  const date = parseIsoDate(dateStr, LEGACY_TIME_ZONE);
  const range = useScheduleRangeOnRoute();

  const query = useReadLaborCostChartData(
    {
      domainId,
      siteId,
      date: date.toISOString(),
      mode,
      range,
      serviceIndex: selectedServiceIndex,
    },
    canSeeSalaries,
  );

  return {
    mode,
    setMode,
    selectedServiceIndex,
    setSelectedServiceIndex,
    isFetching: query.isFetching,
    chartData: query.data,
    isEnabled: canSeeSalaries,
  };
}
